// F-001: Design System Dashboard — bg-teal-50/bg-green-50 headers instead of solid bg-teal-600/bg-green-600
// F-002: Cards bg-white/70 instead of bg-white/20
// F-004: Responsive header flex-col sm:flex-row
// F-005: Responsive key list
// F-007: aria-labels on icon buttons
// F-008: Modal Escape + click-outside
// F-010: staleTime 2min (in hooks)
// R-001: Pagination
// R-005: Skeleton loading
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Key,
  Plus,
  Copy,
  Check,
  AlertTriangle,
  X,
  Trash2,
  Shield,
  Zap,
  Clock,
  Activity,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { Button, Badge } from '@/components/ui-v2';
import { notifications } from '@/lib/notifications';
import { confirm } from '@/stores/confirmStore';
import {
  useApiAccessConfig,
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  useDeleteApiKey,
} from '../hooks/useApiKeys';
import type { CreateApiKeyInput } from '../api/api-keys.api';

/**
 * Scopes granulaires groupés par ressource
 */
const SCOPE_GROUPS = [
  {
    resource: 'invoice',
    labelKey: 'admin.apiKeys.scopes.invoices',
    fallbackLabel: 'Factures',
    readScope: 'read:invoice',
    writeScope: 'write:invoice',
  },
  {
    resource: 'quote',
    labelKey: 'admin.apiKeys.scopes.quotes',
    fallbackLabel: 'Devis',
    readScope: 'read:quote',
    writeScope: 'write:quote',
  },
  {
    resource: 'client',
    labelKey: 'admin.apiKeys.scopes.clients',
    fallbackLabel: 'Clients',
    readScope: 'read:client',
    writeScope: 'write:client',
  },
  {
    resource: 'product',
    labelKey: 'admin.apiKeys.scopes.products',
    fallbackLabel: 'Produits',
    readScope: 'read:product',
    writeScope: 'write:product',
  },
  {
    resource: 'payment',
    labelKey: 'admin.apiKeys.scopes.payments',
    fallbackLabel: 'Paiements',
    readScope: 'read:payment',
    writeScope: 'write:payment',
  },
  {
    resource: 'credit-note',
    labelKey: 'admin.apiKeys.scopes.creditNotes',
    fallbackLabel: 'Avoirs',
    readScope: 'read:credit-note',
    writeScope: 'write:credit-note',
  },
  {
    resource: 'dashboard',
    labelKey: 'admin.apiKeys.scopes.dashboard',
    fallbackLabel: 'Tableau de bord',
    readScope: 'read:dashboard',
    writeScope: null, // Read-only
  },
] as const;

const KEYS_PER_PAGE = 10;

/**
 * F-008: Hook pour fermer un modal avec Escape
 */
function useEscapeKey(isOpen: boolean, onClose: () => void) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);
}

/**
 * R-005: Skeleton pour la liste des clés API
 */
function ApiKeysSkeleton() {
  return (
    <div className="px-4 sm:px-8 pt-8 pb-12 space-y-6">
      {/* Header skeleton */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-teal-100/50 rounded-2xl animate-pulse" />
          <div className="space-y-2">
            <div className="h-7 w-40 bg-gray-200 rounded-xl animate-pulse" />
            <div className="h-4 w-64 bg-gray-100 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="h-10 w-40 bg-teal-100/50 rounded-2xl animate-pulse" />
      </div>

      {/* Rate limit info skeleton */}
      <div className="bg-teal-50/20 rounded-2xl p-5 border border-teal-200/30">
        <div className="flex items-start gap-4">
          <div className="w-9 h-9 bg-teal-100/50 rounded-xl animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-5 w-48 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-4 w-72 bg-gray-100 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>

      {/* Key list skeleton */}
      <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-white/30">
          <div className="h-6 w-36 bg-gray-200 rounded-lg animate-pulse" />
        </div>
        <div className="divide-y divide-white/20">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-4 sm:px-6 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" />
                  </div>
                  <div className="h-7 w-48 bg-gray-100 rounded-xl animate-pulse" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {Array.from({ length: 4 }).map((_, j) => (
                      <div key={j} className="h-5 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                  <div className="h-3 w-56 bg-gray-50 rounded animate-pulse" />
                </div>
                <div className="h-8 w-24 bg-red-100/50 rounded-xl animate-pulse self-end sm:self-center" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ApiKeysManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // R-001: Pagination state
  const [page, setPage] = useState(1);

  // TanStack Query — données serveur
  const { data: accessConfig, isLoading: configLoading } = useApiAccessConfig();
  const { data: keysResult, isLoading: keysLoading, isFetching } = useApiKeys(page, KEYS_PER_PAGE);
  const createMutation = useCreateApiKey();
  const revokeMutation = useRevokeApiKey();
  const deleteMutation = useDeleteApiKey();

  // Extraire données paginées
  const apiKeys = keysResult?.data ?? [];
  const pagination = keysResult?.pagination;

  // État UI local uniquement
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState<{
    name: string;
    scopes: string[];
    isTest: boolean;
    expiresAt: string;
  }>({
    name: '',
    scopes: ['facturx'],
    isTest: false,
    expiresAt: '',
  });
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [scopeMode, setScopeMode] = useState<'preset' | 'granular'>('preset');

  // F-008: Refs pour click-outside
  const createModalRef = useRef<HTMLDivElement>(null);
  const successModalRef = useRef<HTMLDivElement>(null);

  const loading = configLoading || keysLoading;

  // F-008: Escape key handlers
  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setScopeMode('preset');
  }, []);

  const closeSuccessModal = useCallback(() => {
    setCreatedKey(null);
    setShowCreateModal(false);
  }, []);

  useEscapeKey(showCreateModal && !createdKey, closeCreateModal);
  useEscapeKey(!!createdKey, closeSuccessModal);

  // F-002: Card classes — bg-white/70 (Dashboard context, NOT bg-white/20)
  const cardClasses = `bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  // === Handlers ===

  const handleCreateKey = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newKeyData.name.trim()) {
      notifications.error(t('admin.apiKeys.enterNameError'));
      return;
    }

    const input: CreateApiKeyInput = {
      name: newKeyData.name,
      scopes: newKeyData.scopes.length > 0 ? newKeyData.scopes : ['facturx'],
      isTest: newKeyData.isTest,
      expiresAt: newKeyData.expiresAt || undefined,
    };

    createMutation.mutate(input, {
      onSuccess: (result) => {
        setCreatedKey(result.key);
        setNewKeyData({ name: '', scopes: ['facturx'], isTest: false, expiresAt: '' });
        setScopeMode('preset');
        setPage(1); // Revenir à la page 1 après création
        notifications.created(t('admin.apiKeys.entityName'));
      },
    });
  }, [newKeyData, createMutation, t]);

  const handleRevokeKey = useCallback(async (keyId: string, keyName: string) => {
    const confirmed = await confirm({
      title: t('admin.apiKeys.revokeTitle', { defaultValue: 'Révoquer la clé API' }),
      message: t('admin.apiKeys.revokeConfirm', { name: keyName }),
      confirmText: t('admin.apiKeys.revoke'),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annuler' }),
      variant: 'warning',
    });
    if (!confirmed) return;

    revokeMutation.mutate(keyId, {
      onSuccess: () => {
        notifications.success(t('admin.apiKeys.revokedSuccess'));
      },
    });
  }, [revokeMutation, t]);

  const handleDeleteKey = useCallback(async (keyId: string, keyName: string) => {
    const confirmed = await confirm({
      title: t('admin.apiKeys.deleteTitle', { defaultValue: 'Supprimer la clé API' }),
      message: t('admin.apiKeys.deleteConfirm', { name: keyName }),
      confirmText: t('admin.apiKeys.deleteBtn'),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annuler' }),
      variant: 'danger',
    });
    if (!confirmed) return;

    deleteMutation.mutate(keyId, {
      onSuccess: () => {
        notifications.deleted(t('admin.apiKeys.entityName'));
      },
    });
  }, [deleteMutation, t]);

  const copyToClipboard = useCallback(async (text: string, keyId?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (keyId) {
        setCopiedKeyId(keyId);
        setTimeout(() => setCopiedKeyId(null), 2000);
      }
      notifications.copied(t('admin.apiKeys.entityName'));
    } catch {
      notifications.error(t('admin.apiKeys.copyError'));
    }
  }, [t]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('admin.apiKeys.never');
    return new Date(dateString).toLocaleString(i18n.language || 'fr-FR');
  };

  // === Scopes helpers ===

  const toggleScope = (scope: string) => {
    setNewKeyData((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter((s) => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const selectAllScopes = () => {
    const allScopes: string[] = [];
    SCOPE_GROUPS.forEach((group) => {
      allScopes.push(group.readScope);
      if (group.writeScope) allScopes.push(group.writeScope);
    });
    setNewKeyData((prev) => ({ ...prev, scopes: allScopes }));
  };

  const clearAllScopes = () => {
    setNewKeyData((prev) => ({ ...prev, scopes: [] }));
  };

  const formatScopeDisplay = (scopes: string[]) => {
    if (scopes.includes('*')) return t('admin.apiKeys.scopes.fullAccess', { defaultValue: 'Accès complet' });
    if (scopes.includes('facturx')) return t('admin.apiKeys.scopeFacturxShort', { defaultValue: 'FacturX (standard)' });
    if (scopes.includes('webhooks')) return t('admin.apiKeys.scopeWebhooksShort', { defaultValue: 'Webhooks' });
    const count = scopes.length;
    return t('admin.apiKeys.scopeCount', { count, defaultValue: '{{count}} scope(s)' });
  };

  // F-008: Click outside handler
  const handleBackdropClick = useCallback((e: React.MouseEvent, ref: React.RefObject<HTMLDivElement | null>, onClose: () => void) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      onClose();
    }
  }, []);

  // === Render ===

  // R-005: Skeleton loading au lieu de spinner
  if (loading) {
    return <ApiKeysSkeleton />;
  }

  return (
    <div className="px-4 sm:px-8 pt-8 pb-12 space-y-6">
      {/* F-004: Header responsive — flex-col sm:flex-row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Key className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.apiKeys.title')}</h1>
            <p className="text-gray-500 mt-0.5">
              {t('admin.apiKeys.subtitle')}
            </p>
          </div>
        </div>

        {accessConfig?.hasAccess && (
          <Button
            variant="secondary"
            onClick={() => setShowCreateModal(true)}
            icon={<Plus className="w-4 h-4" />}
            aria-label={t('admin.apiKeys.create')}
          >
            {t('admin.apiKeys.create')}
          </Button>
        )}
      </div>

      {/* Message si l'accès API n'est pas autorisé */}
      {accessConfig && !accessConfig.hasAccess && (
        <div className="bg-yellow-50/50 backdrop-blur-sm border border-yellow-200/50 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-yellow-100 rounded-xl">
              <Shield className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-900 mb-2">{t('admin.apiKeys.noAccessTitle')}</h4>
              <p className="text-sm text-yellow-800 mb-2">
                {t('admin.apiKeys.noAccessCurrentPlan', { plan: accessConfig.currentSubscription.toUpperCase() })}
              </p>
              <p className="text-sm text-yellow-800 mb-4">
                {t('admin.apiKeys.requiredPlansValue', { plans: accessConfig.allowedSubscriptions.map((s) => s.toUpperCase()).join(', ') })}
              </p>
              <Button variant="secondary" onClick={() => navigate('/settings/subscription')}>
                {t('admin.apiKeys.upgradeSubscription')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info Rate Limit */}
      {accessConfig && (
        <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-teal-100 rounded-xl">
              <Zap className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h4 className="font-semibold text-teal-900">{t('admin.apiKeys.rateLimitTitle')}</h4>
              <p className="text-sm text-teal-800 mt-1">
                {t('admin.apiKeys.rateLimitDesc', { plan: accessConfig.currentSubscription.toUpperCase(), limit: accessConfig.rateLimit })}
              </p>
              <p className="text-xs text-teal-700 mt-1">
                {t('admin.apiKeys.upgradeSubscription')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Liste des clés */}
      {apiKeys.length === 0 && !isFetching ? (
        <div className={cardClasses}>
          <div className="text-center py-8">
            <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
              <Key className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.apiKeys.noKeys')}</h3>
            <p className="text-gray-600 mb-6">{t('admin.apiKeys.noKeysDescription')}</p>
            {accessConfig?.hasAccess && (
              <div className="flex justify-center">
                <Button
                  variant="secondary"
                  onClick={() => setShowCreateModal(true)}
                  icon={<Plus className="w-4 h-4" />}
                >
                  {t('admin.apiKeys.create')}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={`${cardClasses} p-0 overflow-hidden`}>
          <div className="px-4 sm:px-6 py-4 border-b border-white/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Key className="w-5 h-5 text-teal-600" />
              {t('admin.apiKeys.yourKeys')}
              {pagination && (
                <span className="text-sm font-normal text-gray-500">
                  ({pagination.total})
                </span>
              )}
            </h3>
            {/* R-001: Loading indicator during page change */}
            {isFetching && !keysLoading && (
              <div className="h-4 w-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
            )}
          </div>
          {/* F-005: Liste responsive */}
          <ul className="divide-y divide-white/20">
            {apiKeys.map((key) => (
              <li key={key.id} className="px-4 sm:px-6 py-4 hover:bg-white/30 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-lg font-medium text-gray-900 truncate">{key.name}</h3>
                      {!key.isActive && <Badge variant="alert">{t('admin.apiKeys.revoked')}</Badge>}
                      {key.expiresAt && new Date(key.expiresAt) < new Date() && (
                        <Badge variant="warning">{t('admin.apiKeys.expired')}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                      <code className="bg-gray-100 px-3 py-1.5 rounded-xl font-mono text-xs truncate">
                        {key.keyPreview}
                      </code>
                      <button
                        onClick={() => copyToClipboard(key.keyPreview || '', key.id)}
                        className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors flex-shrink-0"
                        aria-label={t('admin.apiKeys.copyKey')}
                      >
                        {copiedKeyId === key.id ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {/* F-005: Grid responsive — 1 col mobile, 2 cols tablette, 4 cols desktop */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500">{t('admin.apiKeys.permissions')}</span>
                        <span className="text-gray-900 truncate">{formatScopeDisplay(key.scopes)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500">{t('admin.apiKeys.rateLimit')}</span>
                        <span className="text-gray-900">{t('admin.apiKeys.rateLimitValue', { value: key.rateLimit })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500">{t('admin.apiKeys.usages')}</span>
                        <span className="text-gray-900">-</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500">{t('admin.apiKeys.lastUsed')}</span>
                        <span className="text-gray-900 truncate">{formatDate(key.lastUsedAt)}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      {t('admin.apiKeys.createdAt')} {formatDate(key.createdAt)}
                      {key.expiresAt && ` • ${t('admin.apiKeys.expiresAt')} ${formatDate(key.expiresAt)}`}
                    </div>
                  </div>
                  <div className="flex-shrink-0 self-end sm:self-center">
                    {key.isActive ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRevokeKey(key.id, key.name)}
                        disabled={revokeMutation.isPending}
                        aria-label={t('admin.apiKeys.revoke')}
                      >
                        {t('admin.apiKeys.revoke')}
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteKey(key.id, key.name)}
                        icon={<Trash2 className="w-4 h-4" />}
                        disabled={deleteMutation.isPending}
                        aria-label={t('admin.apiKeys.deleteBtn')}
                      >
                        {t('admin.apiKeys.deleteBtn')}
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* R-001: Pagination controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-4 sm:px-6 py-3 border-t border-white/30 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {t('admin.apiKeys.paginationInfo', {
                  current: pagination.page,
                  total: pagination.totalPages,
                  count: pagination.total,
                  defaultValue: 'Page {{current}} sur {{total}} ({{count}} clés)',
                })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-xl text-gray-500 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label={t('admin.apiKeys.previousPage', { defaultValue: 'Page précédente' })}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm font-medium text-gray-700 min-w-[3rem] text-center">
                  {pagination.page}/{pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  className="p-2 rounded-xl text-gray-500 hover:text-teal-600 hover:bg-teal-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label={t('admin.apiKeys.nextPage', { defaultValue: 'Page suivante' })}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Création — F-001: Dashboard Design System headers */}
      {showCreateModal && !createdKey && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => handleBackdropClick(e, createModalRef, closeCreateModal)}
          role="dialog"
          aria-modal="true"
          aria-label={t('admin.apiKeys.createModalTitle')}
        >
          <div ref={createModalRef} className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-white/50 max-h-[90vh] overflow-y-auto">
            {/* F-001: Header modal — Dashboard context (bg-teal-50, text-teal-900 au lieu de bg-teal-600 text-white) */}
            <div className="bg-teal-50 border-b border-teal-200/50 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-xl">
                    <Key className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{t('admin.apiKeys.createModalTitle')}</h3>
                </div>
                <button
                  onClick={closeCreateModal}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  aria-label={t('common.actions.close', { defaultValue: 'Fermer' })}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenu modal */}
            <form onSubmit={handleCreateKey} className="p-6 space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.apiKeys.keyName')}
                </label>
                <input
                  type="text"
                  value={newKeyData.name}
                  onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                  className={inputClasses}
                  placeholder={t('admin.apiKeys.keyNamePlaceholderShort')}
                  required
                  aria-label={t('admin.apiKeys.keyName')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin.apiKeys.keyNameHint')}
                </p>
              </div>

              {/* Scopes — Mode preset vs granulaire */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.apiKeys.permissionsLabel')}
                </label>

                {/* Toggle preset / granulaire */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setScopeMode('preset');
                      setNewKeyData((prev) => ({ ...prev, scopes: ['facturx'] }));
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-xl transition-all duration-300 ${
                      scopeMode === 'preset'
                        ? 'bg-teal-100 text-teal-700 border border-teal-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                    aria-label={t('admin.apiKeys.presetMode', { defaultValue: 'Mode presets' })}
                  >
                    {t('admin.apiKeys.presetLabel', { defaultValue: 'Presets' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setScopeMode('granular');
                      setNewKeyData((prev) => ({ ...prev, scopes: [] }));
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-xl transition-all duration-300 ${
                      scopeMode === 'granular'
                        ? 'bg-teal-100 text-teal-700 border border-teal-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                    aria-label={t('admin.apiKeys.granularMode', { defaultValue: 'Mode granulaire' })}
                  >
                    {t('admin.apiKeys.granularLabel', { defaultValue: 'Granulaire' })}
                  </button>
                </div>

                {scopeMode === 'preset' ? (
                  /* Mode preset */
                  <div className="space-y-2">
                    {[
                      { value: 'facturx', label: t('admin.apiKeys.scopeFacturxShort', { defaultValue: 'FacturX (standard)' }), desc: t('admin.apiKeys.apiFacturxDesc', { defaultValue: 'Factures, devis, clients, produits, dashboard' }) },
                      { value: 'webhooks', label: t('admin.apiKeys.scopeWebhooksShort', { defaultValue: 'Webhooks' }), desc: t('admin.apiKeys.webhooksDesc', { defaultValue: 'Lecture événements factures, devis, paiements, avoirs' }) },
                      { value: '*', label: t('admin.apiKeys.scopes.fullAccess', { defaultValue: 'Accès complet' }), desc: t('admin.apiKeys.fullAccessDesc', { defaultValue: 'Toutes les permissions (réservé aux propriétaires)' }) },
                    ].map((preset) => (
                      <label key={preset.value} className="flex items-start gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer">
                        <input
                          type="radio"
                          name="scopePreset"
                          checked={newKeyData.scopes.length === 1 && newKeyData.scopes[0] === preset.value}
                          onChange={() => setNewKeyData((prev) => ({ ...prev, scopes: [preset.value] }))}
                          className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-teal-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{preset.label}</span>
                          <p className="text-xs text-gray-500">{preset.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  /* Mode granulaire */
                  <div className="space-y-1">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-gray-500">
                        {t('admin.apiKeys.scopeSelectedCount', {
                          count: newKeyData.scopes.length,
                          defaultValue: '{{count}} scope(s) sélectionné(s)',
                        })}
                      </span>
                      <div className="flex gap-2">
                        <button type="button" onClick={selectAllScopes} className="text-xs text-teal-600 hover:text-teal-700 font-medium">
                          {t('admin.apiKeys.selectAll', { defaultValue: 'Tout sélectionner' })}
                        </button>
                        <span className="text-gray-300">|</span>
                        <button type="button" onClick={clearAllScopes} className="text-xs text-gray-500 hover:text-gray-700 font-medium">
                          {t('admin.apiKeys.clearAll', { defaultValue: 'Tout décocher' })}
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-100">
                      {SCOPE_GROUPS.map((group) => (
                        <div key={group.resource} className="px-4 py-2.5 hover:bg-gray-50/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-800">
                              {t(group.labelKey, { defaultValue: group.fallbackLabel })}
                            </span>
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={newKeyData.scopes.includes(group.readScope)}
                                  onChange={() => toggleScope(group.readScope)}
                                  className="h-3.5 w-3.5 text-teal-600 rounded focus:ring-teal-500"
                                />
                                <span className="text-xs text-gray-600">{t('admin.apiKeys.read', { defaultValue: 'Lecture' })}</span>
                              </label>
                              {group.writeScope && (
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={newKeyData.scopes.includes(group.writeScope)}
                                    onChange={() => toggleScope(group.writeScope!)}
                                    className="h-3.5 w-3.5 text-teal-600 rounded focus:ring-teal-500"
                                  />
                                  <span className="text-xs text-gray-600">{t('admin.apiKeys.write', { defaultValue: 'Écriture' })}</span>
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Clé de test */}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newKeyData.isTest}
                    onChange={(e) => setNewKeyData({ ...newKeyData, isTest: e.target.checked })}
                    className="h-4 w-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">{t('admin.apiKeys.testKey')}</span>
                </label>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.apiKeys.expirationLabel')}
                </label>
                <input
                  type="datetime-local"
                  value={newKeyData.expiresAt}
                  onChange={(e) => setNewKeyData({ ...newKeyData, expiresAt: e.target.value })}
                  className={inputClasses}
                  aria-label={t('admin.apiKeys.expirationLabel')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin.apiKeys.expirationHint')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={closeCreateModal}
                  className="flex-1"
                >
                  {t('common.actions.cancel')}
                </Button>
                <Button
                  variant="secondary"
                  type="submit"
                  className="flex-1"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? t('common.actions.creating', { defaultValue: 'Création...' }) : t('common.actions.create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Clé créée — F-001: Dashboard Design System header */}
      {createdKey && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => handleBackdropClick(e, successModalRef, closeSuccessModal)}
          role="dialog"
          aria-modal="true"
          aria-label={t('admin.apiKeys.createdTitle')}
        >
          <div ref={successModalRef} className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-white/50 max-h-[90vh] overflow-y-auto">
            {/* F-001: Header modal — Dashboard context (bg-green-50, text-gray-900 au lieu de bg-green-600 text-white) */}
            <div className="bg-green-50 border-b border-green-200/50 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-xl">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{t('admin.apiKeys.createdTitle')}</h3>
                </div>
                <button
                  onClick={closeSuccessModal}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  aria-label={t('common.actions.close', { defaultValue: 'Fermer' })}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenu modal */}
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50/50 border border-yellow-200/50 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-yellow-900">{t('admin.apiKeys.importantLabel')}</p>
                    <p className="text-sm text-yellow-800 mt-1">
                      {t('admin.apiKeys.importantMessage')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded-2xl">
                <code className="text-sm break-all font-mono">{createdKey}</code>
              </div>

              <Button
                variant="secondary"
                onClick={() => copyToClipboard(createdKey)}
                className="w-full"
                icon={<Copy className="w-4 h-4" />}
                aria-label={t('admin.apiKeys.copyKey')}
              >
                {t('admin.apiKeys.copyKey')}
              </Button>
              <Button
                variant="ghost"
                onClick={closeSuccessModal}
                className="w-full"
              >
                {t('admin.apiKeys.copiedClose')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
